import React, { useContext, useRef } from 'react';
import { Form, FormSpy, Field } from 'react-final-form';
import { createForm } from 'final-form';
import { Grid, makeStyles } from '@material-ui/core';
import { TextField } from 'mui-rff';
import {
  fieldLengthValidator,
  isFieldContainsOnlySpaces
} from '../../../core/utils';
import { ReferenceArrayInput } from 'ra-ui-materialui';
import GridInputChoices from '../../users/userauthorization/GridInputChoices';
import GridInput from '../../../core/components/inputs/GridInput';
import { validateFundInvestorTagNameRff } from './utils';
import { CreationFundInvestorTagContext } from './CreationFundInvestorTagContext';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import FundInvestorApi from '../../../core/apis/FundInvestorApi';
import FundInvestorTagApi from '../../../core/apis/FundInvestorTagApi';
import { TextInput } from 'react-admin';
import CheckBoxField from '../../../core/components/CheckBoxField';

const tagNameMaxLength = 30;
const tagDescriptionMaxLength = 1000;

const useStyle = makeStyles({
  formGroup: {
    marginBlock: '16px'
  },
  containerFlex: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0px',
    margin: 0
  },
  checkBoxFlex: {
    display: 'flex',
    alignSelf: 'end',
    marginLeft: '22px',
    marginBottom: '3px'
  }
});

const TagDetailsStep = ({
  onSubmitEnd,
  setIsNextAvailable,
  setIsSpinnerOnNextButton
}) => {
  const classes = useStyle();
  const { setFundInvestorTagContext } = useContext(
    CreationFundInvestorTagContext
  );
  const notifyServerError = useNotifyServerError();

  const validateFunds = value => {
    if (Array.isArray(value) && value.length < 1) {
      return 'At least one Fund must be selected';
    }
    return undefined;
  };

  const mapInvestorsToSelectedFunds = async selectedFundIds => {
    // The API expects a string as parameter formatted like so: [F1,F2,...]
    try {
      await FundInvestorApi.getByFundIds(selectedFundIds)
        .then(allFunds => {
          /**
           * mapFundInvestor: {
           *   fundId1: {
           *     fundName: "fundName 1",
           *     investors: [{ investorId: "investorId1", investorName: "investorName1", ... }]
           *   },
           *   fundId2 : { ... }
           * }
           */
          const mapFundInvestors = allFunds.reduce((acc, fi) => {
            if (!acc[fi.fundId]) {
              acc[fi.fundId] = { investors: [], fundName: fi.fundName };
            }
            acc[fi.fundId].investors.push({
              investorId: fi.investorId,
              investorName: fi.investorName
            });
            return acc;
          }, {});

          const mapFundInvestorSortedByFund = Object.entries(
            mapFundInvestors
          ).sort((a, b) => {
            const [, value1] = a;
            const [, value2] = b;
            return value1.fundName
              .toLowerCase()
              .localeCompare(value2.fundName.toLowerCase());
          });
          setFundInvestorTagContext(prevState => ({
            ...prevState,
            mapFundInvestorSortedByFund
          }));
        })
        .finally(() => setIsSpinnerOnNextButton(false));
    } catch (e) {
      notifyServerError(e, undefined);
    }
  };

  const onSubmit = async formValues => {
    setIsSpinnerOnNextButton(true);
    const payloadTag = {
      name: formValues.name,
      description: formValues.description,
      tagPrivacy: formValues.tagPrivacy
    };
    await FundInvestorTagApi.create(payloadTag)
      .then(async response => {
        setFundInvestorTagContext(() => ({
          id: response.id,
          name: formValues.name,
          description: formValues.description,
          tagPrivacy: formValues.tagPrivacy
        }));
        await mapInvestorsToSelectedFunds(formValues.funds);
        onSubmitEnd();
      })
      .catch(err => {
        notifyServerError(err);
        setIsSpinnerOnNextButton(false);
      });
  };

  const formRef = useRef(
    createForm({
      onSubmit: onSubmit
    })
  );

  const required = value => (value ? undefined : 'Required');
  const composeValidators = (...validators) => (value, values, fieldState) =>
    validators.reduce(
      (error, validator) => error || validator(value, values, fieldState),
      undefined
    );

  const referenceArrayInputProps = {
    label: '',
    optionTextLabel: 'Fund Name',
    optionValueLabel: 'Fund ID',
    resource: 'funds',
    reference: 'funds',
    source: 'funds',
    validate: [validateFunds],
    perPage: -1,
    sort: { field: 'name', order: 'ASC' },
    additionalColumns: [
      { label: 'Status', name: 'status', isStatusColumn: true }
    ]
  };

  return (
    <Form form={formRef.current}>
      {({ handleSubmit, valid, submitting }) => (
        <>
          <FormSpy
            subscription={{ valid, submitting }}
            onChange={props => {
              setTimeout(() => {
                setIsNextAvailable(props.valid && !props.submitting);
              }, 1);
            }}
          />
          <form onSubmit={handleSubmit} id="formTagDetails" noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className={`${classes.containerFlex}`}>
                  <Grid item xs={3} className={classes.gridItem}>
                    <TextField
                      name="name"
                      label="Tag Name"
                      fieldProps={{
                        validate: composeValidators(
                          fieldLengthValidator(tagNameMaxLength),
                          required,
                          value => validateFundInvestorTagNameRff(value)
                        )
                      }}
                      required
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid item xs={9} className={classes.checkBoxFlex}>
                    <Field
                      name="tagPrivacy"
                      component={CheckBoxField}
                      label="Private Tag"
                      variant="standard"
                      type="checkbox"
                      initialValue={false}
                    />
                  </Grid>
                </div>
                <Grid item xs={12}>
                  <TextInput
                    source="description"
                    label="Description"
                    validate={[
                      isFieldContainsOnlySpaces,
                      fieldLengthValidator(tagDescriptionMaxLength)
                    ]}
                    options={{ autoComplete: 'off' }}
                    variant="standard"
                    helperText={false}
                    multiline={true}
                    rows="3"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ReferenceArrayInput
                  {...referenceArrayInputProps}
                  filter={{ onlyFundsWithFundInvestorRelation: 'true' }}
                >
                  <GridInputChoices emptyText="There are no Funds to be displayed.">
                    <GridInput
                      height={`${document.documentElement.scrollHeight -
                        480}px`}
                    />
                  </GridInputChoices>
                </ReferenceArrayInput>
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </Form>
  );
};

export default TagDetailsStep;
